import { MinusCircle, Pencil, PlusCircle } from "lucide-react"
import { NodeKey } from "lexical"
import { ContentSummary, GridSlot, GridSlotSize } from "common/types/graphql"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import React, { PropsWithChildren, useCallback } from "react"
import { ImmutableGridSlotProps, ImmutableGridSlot } from "./ImmutableGridSlot"
import { Bugsnag } from "common/lib/bugsnag"
import { INDEX_FOR_NO_GRID_SLOT } from "modules/editor/blocks/Grid/Grid"

export interface MutableGridSlotProps extends Omit<ImmutableGridSlotProps, "isOver"> {
  /** The index of the grid slot. */
  index: number

  gridNodeKey?: NodeKey

  /**
   * A handler for the clear action.
   *
   * @param index The index of the grid slot.
   */
  onClear?: (index: number) => void

  /**
   * A handler for the drop action.
   *
   * @param index The index of the grid slot.
   * @param item The dropped item.
   */
  onDrop?: (index: number, item: DragItem) => void

  /**
   * A handler for swapping slots.
   *
   * @param a The index of the first slot.
   * @param b The index of the second slot.
   */
  onSwap?: (a: number, b: number, item: DragItem) => void

  /**
   * A handler for the edit action.
   *
   * @param index The index of the grid slot.
   */
  onEdit?: (index: number) => void
  /**
   * A handler for the add action.
   *
   * @param index The index of the grid slot.
   */
  onAdd?: (index: number) => void

  /**
   * Handler for when something is being dragged over this
   * @param index The index of the grid slot
   */
  onDraggingOver?: (index: number) => void

  /**
   * Boolean for whether something is currently dragged over this
   */
  dragOver?: boolean
}

interface DragItem extends Partial<ContentSummary> {
  index: number
  swappable: boolean
  sourceGridNodeKey?: NodeKey
  itemType?: GridSlot
}

/** A grid slot that can be modified by a user */
export const MutableGridSlot = ({
  index,
  gridNodeKey,
  size = GridSlotSize.Base,
  type = GridSlot.Editorial,
  asPlaceholder = false,
  onClear,
  onDrop,
  onSwap,
  onEdit,
  onAdd,
  children,
  onDraggingOver,
  dragOver = false,
}: PropsWithChildren<MutableGridSlotProps>) => {
  const t = useTranslations("blocks.grid")

  const onClearButtonClick = useCallback(() => {
    onClear && onClear(index)
  }, [onClear, index])

  const onEditButtonClick = useCallback(() => {
    onEdit && onEdit(index)
  }, [onEdit, index])

  const onAddButtonClick = useCallback(() => {
    onAdd && onAdd(index)
  }, [onAdd, index])

  const dragOverHandler = useCallback(
    (ev: React.DragEvent<HTMLDivElement>) => {
      ev.preventDefault()
      onDraggingOver && onDraggingOver(index)
    },
    [onDraggingOver, index],
  )

  const dropHandler = useCallback(
    (ev: React.DragEvent<HTMLDivElement>) => {
      ev.preventDefault()
      let item: DragItem
      const dataTransfer = ev.dataTransfer
      const swapData = dataTransfer.getData("gridSlot")
      const addData = dataTransfer.getData("contentSummary")
      const data = swapData || addData

      // Set no grid slot as being dragged over anymore
      onDraggingOver?.(INDEX_FOR_NO_GRID_SLOT)

      if (!data) {
        console.error("Drop handler for MutableGridSlot has no data")
        return
      }

      try {
        item = JSON.parse(data).item
      } catch (e) {
        Bugsnag.notify(e as Error)
        console.error("Failed to parse the drop data", e)
        return
      }

      if (
        item &&
        item.sourceGridNodeKey === gridNodeKey &&
        item.index !== index &&
        item.itemType === type
      ) {
        onSwap && onSwap(item.index, index, item)
      }

      /*
      Drop an item if it's dragged from search results or another grid block.
    */
      if (item && item.sourceGridNodeKey !== gridNodeKey && item.itemType === type) {
        onDrop && onDrop(index, item)
      }
    },
    [gridNodeKey, index, onDraggingOver, onSwap, onDrop, type],
  )

  const dragStartHandler = useCallback(
    (ev: React.DragEvent<HTMLDivElement>) => {
      /**
       * Clears previous data, shouldn't do anything in normal browser use, but, our cypress tests
       * reuse the same data object, so it needs to be cleared to stop tests interacting with each other,
       * due to jsdom not fully supporting dataTransfer.
       */
      ev.dataTransfer.clearData("gridSlot")
      ev.dataTransfer.setData(
        "gridSlot",
        JSON.stringify({
          item: { index, sourceGridNodeKey: gridNodeKey, swappable: true, itemType: type },
        }),
      )
    },
    [index, gridNodeKey, type],
  )

  return (
    <ImmutableGridSlot
      size={size}
      type={type}
      asPlaceholder={asPlaceholder}
      isOver={dragOver}
      handleDrop={dropHandler}
      handleDragOver={dragOverHandler}
      handleDragStart={dragStartHandler}
    >
      {children}
      <div className="absolute right-0 hidden border-collapse shadow-md group-hover:flex">
        {onEdit && (
          <button
            onClick={onEditButtonClick}
            aria-label={t("customise_edit")}
            className="border border-r-0 border-gray-300 bg-white p-2"
          >
            {t("edit")} <Pencil className="inline-block size-4" />
          </button>
        )}
        {onAdd && (
          <button
            onClick={onAddButtonClick}
            aria-label={t("add_custom_content")}
            className="border border-r-0 border-gray-300 bg-white p-2"
          >
            {t("add_custom_content")} <PlusCircle className="inline-block size-6" />
          </button>
        )}
        {onClear && (
          <button
            onClick={onClearButtonClick}
            aria-label={t("clear_slot")}
            className="border border-gray-300 bg-white p-2"
          >
            <MinusCircle className="size-6 text-red-500" />
          </button>
        )}
      </div>
    </ImmutableGridSlot>
  )
}
